import { queryClient } from "@/providers/LocalQueryClientProvider";
import { APP_CONFIG_QUERY_KEY } from "@/queries/useAppConfigQuery";
import { subscribeSessionStarting, subscribeTokenEstablished } from "@/lib/sessionChannel";
import { resetCardVerification } from "@/store/cardVerificationStore";

/**
 * Re-read /api/config whenever a new access token lands (CT-36 QA round 1; CT-136).
 *
 * The scopes the dashboard gates on are a property of the TOKEN (pbx-api re-compiles them at every
 * refresh, and sip.io-v2 clamps them), but the config is cached for a flat staleTime with no
 * dependency on it. This used to re-ask the /v1/whoami probe for the same reason: an answer taken
 * against a dead token stuck, and the migrated modules stayed missing from the sidebar until a full
 * page load. It also lands a role/permission change at the next rotation instead of the next reload.
 *
 * invalidateQueries refetches in the background and keeps the previous data meanwhile, so the
 * routine 15-minute rotation costs one request and never blanks a scope-gated screen.
 *
 * A NEW session (no token before) drops the whole cache instead: a different user may be signing
 * in on the same tab, and the shell must not gate on the last user's config (QA #7).
 */
export function initSessionQuerySync(): () => void {
    const stopStarting = subscribeSessionStarting(() => {
        queryClient.clear();
        resetCardVerification();
    });
    const stopEstablished = subscribeTokenEstablished(() => {
        void queryClient.invalidateQueries({ queryKey: APP_CONFIG_QUERY_KEY });
    });
    return () => {
        stopStarting();
        stopEstablished();
    };
}
